import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, FieldValues, ControllerRenderProps } from 'react-hook-form';
import {
  Container,
  Paper,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Box,
  IconButton
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import ClearIcon from '@mui/icons-material/Clear';

// Services
import { getAllWarehouses, addInventoryItem, createWarehouse } from '../services/inventoryService';
import { getMainCategories, getSubcategories } from '../services/categoryService';
import { uploadImage } from '../services/cloudinaryService';
import { uploadProductImage, prepareImagesForFirestore } from '../services/productImageService';
import MultipleImageUpload from '../components/common/MultipleImageUpload';
import { convertToBase64 } from '../utils/imageUtils';
import { postProductToFacebook } from '../services/facebookService';
import { getStoreSettings } from '../services/storeSettingsService';

// Types
import { WarehouseQuantity, Warehouse, ProductImage } from '../utils/types';

// Form data type
type FormData = {
  name: string;
  sku: string;
  barcode: string;
  category: string;
  categoryId: string;
  subcategory?: string;
  subcategoryId?: string;
  purchasePrice: number;
  sellingPrice: number;
  // أسعار متعددة للمنتج
  posPrice?: number; // سعر نقطة البيع (POS)
  storePrice?: number; // سعر المتجر الإلكتروني
  wholesalePrice?: number; // سعر الجملة
  quantity: number;
  minQuantity: number;
  description: string;
  supplier: string;
  warehouseId: string;
  warehouseName: string;
  warehouseQuantities: WarehouseQuantity[];
  image: string;
  imageUrl?: string;       // رابط الصورة الرئيسي
  imgbbUrl?: string;       // رابط ImgBB
  imagekitUrl?: string;    // رابط ImageKit.io
  additionalImages?: ProductImage[]; // مصفوفة من الصور الإضافية للمنتج
  showInStore: boolean;
  featured: boolean; // منتج مميز
  bestSeller: boolean; // منتج الأكثر مبيعًا
  postToFacebook?: boolean; // خيار النشر على فيسبوك
};

/**
 * صفحة إضافة عنصر جديد للمخزون
 */
const NewAddInventoryPage: React.FC = () => {
  // React Router hooks
  const navigate = useNavigate();

  // State variables
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseQuantities, setWarehouseQuantities] = useState<WarehouseQuantity[]>([]);
  const [categories, setCategories] = useState<{id: string, name: string}[]>([]);
  const [subcategories, setSubcategories] = useState<{id: string, name: string}[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [productImages, setProductImages] = useState<ProductImage[]>([]);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // React Hook Form
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      sku: '',
      barcode: '',
      category: '',
      categoryId: '',
      subcategory: '',
      subcategoryId: '',
      purchasePrice: 0,
      sellingPrice: 0,
      quantity: 0,
      minQuantity: 0,
      description: '',
      supplier: '',
      warehouseId: '',
      warehouseName: '',
      warehouseQuantities: [],
      image: '',
      showInStore: true,
      featured: false,
      bestSeller: false,
      postToFacebook: false
    }
  });

  // Watch for category changes
  const selectedCategory = watch('categoryId');

  // تحميل الفئات عند تحميل الصفحة
  useEffect(() => {
    const loadCategories = async () => {
      try {
        console.log('Loading main categories for product form...');
        // استخدام forceRefresh = true لضمان تحميل البيانات من قاعدة البيانات
        const categoriesData = await getMainCategories(true);
        console.log('Loaded categories:', categoriesData.length);

        if (categoriesData.length === 0) {
          console.warn('No categories found, trying again without force refresh');
          const fallbackData = await getMainCategories(false);
          console.log('Fallback categories loaded:', fallbackData.length);

          setCategories(fallbackData.map(cat => ({
            id: cat.id,
            name: cat.name
          })));
        } else {
          setCategories(categoriesData.map(cat => ({
            id: cat.id,
            name: cat.name
          })));
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        setError('فشل في تحميل الفئات');
      }
    };

    loadCategories();
  }, []);

  // تحميل الفئات الفرعية عند تغيير الفئة الرئيسية
  useEffect(() => {
    const loadSubcategories = async () => {
      // إعادة تعيين الفئات الفرعية عند البدء أو إذا لم يتم تحديد فئة رئيسية
      setSubcategories([]);
      setValue('subcategoryId', '');
      setValue('subcategory', '');

      if (selectedCategory) {
        try {
          console.log('Loading subcategories for category ID:', selectedCategory);
          const subcatsData = await getSubcategories(selectedCategory, true);

          if (subcatsData && Array.isArray(subcatsData) && subcatsData.length > 0) {
            // فلترة البيانات الصالحة فقط
            const validSubcats = subcatsData.filter(subcat => subcat && subcat.id && subcat.name);
            console.log('Received valid subcategories:', validSubcats.length);

            // تعيين الفئات الفرعية الصالحة
            setSubcategories(validSubcats.map(subcat => ({
              id: subcat.id,
              name: subcat.name
            })));
          } else {
            console.log('No subcategories found for category ID:', selectedCategory);
            setSubcategories([]); // التأكد من أن الحالة فارغة
          }
        } catch (error) {
          console.error('Error loading subcategories:', error);
          setSubcategories([]); // التأكد من أن الحالة فارغة عند الخطأ
        }
      }
    };

    loadSubcategories();
  }, [selectedCategory, setValue]);

  // تحميل المخازن عند تحميل الصفحة
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        console.log('Loading warehouses...');
        const warehouseData = await getAllWarehouses();
        console.log('Warehouses loaded:', warehouseData);

        // التحقق من وجود مخازن
        if (!warehouseData || warehouseData.length === 0) {
          console.warn('No warehouses available, creating a default warehouse');

          // إنشاء مخزن افتراضي إذا لم يكن هناك مخازن
          try {
            const defaultWarehouseId = await createWarehouse('المخزن الرئيسي', 'الموقع الافتراضي');
            console.log('Created default warehouse with ID:', defaultWarehouseId);

            // إعادة تحميل المخازن بعد إنشاء المخزن الافتراضي
            const updatedWarehouseData = await getAllWarehouses();
            console.log('Warehouses reloaded after creating default:', updatedWarehouseData);
            setWarehouses(updatedWarehouseData);

            if (updatedWarehouseData.length > 0) {
              console.log('Setting default warehouse:', updatedWarehouseData[0].name);
              setValue('warehouseId', updatedWarehouseData[0].id);
              setValue('warehouseName', updatedWarehouseData[0].name);

              // إضافة كميات افتراضية للمخازن
              const initialQuantities = updatedWarehouseData.map(warehouse => ({
                warehouseId: warehouse.id,
                warehouseName: warehouse.name,
                quantity: 0
              }));
              setWarehouseQuantities(initialQuantities);
              setValue('warehouseQuantities', initialQuantities);
            }
          } catch (createError) {
            console.error('Error creating default warehouse:', createError);
            setError('فشل في إنشاء المخزن الافتراضي');
          }
        } else {
          // تعيين المخازن الموجودة
          setWarehouses(warehouseData);

          console.log('Setting default warehouse:', warehouseData[0].name);
          setValue('warehouseId', warehouseData[0].id);
          setValue('warehouseName', warehouseData[0].name);

          // إضافة كميات افتراضية لجميع المخازن
          const initialQuantities = warehouseData.map(warehouse => ({
            warehouseId: warehouse.id,
            warehouseName: warehouse.name,
            quantity: 0
          }));
          console.log('Setting initial warehouse quantities:', initialQuantities);
          setWarehouseQuantities(initialQuantities);
          setValue('warehouseQuantities', initialQuantities);
        }
      } catch (error) {
        console.error('Error loading warehouses:', error);
        setError('فشل في تحميل المخازن');
      }
    };

    loadWarehouses();
  }, [setValue]);

  // معالجة تغيير كميات المخازن
  const handleWarehouseQuantityChange = (quantities: WarehouseQuantity[]) => {
    console.log('Warehouse quantities changed:', quantities);

    // التأكد من الحفاظ على قيمة الموقع لكل مخزن
    const updatedQuantities = quantities.map(q => {
      // إذا لم يكن هناك موقع محدد، نضيف سلسلة فارغة
      if (!q.location) {
        return { ...q, location: '' };
      }
      return q;
    });

    setWarehouseQuantities(updatedQuantities);
    setValue('warehouseQuantities', updatedQuantities);

    // حساب الكمية الإجمالية
    const totalQuantity = updatedQuantities.reduce((sum, item) => sum + (item.quantity || 0), 0);
    console.log('Total quantity calculated:', totalQuantity);
    setValue('quantity', totalQuantity);
  };

  // معالجة اختيار الصورة
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      // إعادة تعيين رسالة الخطأ قبل معالجة الصورة الجديدة
      setError('');

      const file = event.target.files[0];
      console.log('تم اختيار صورة:', file.name, 'حجم:', (file.size / 1024).toFixed(2) + 'KB', 'نوع:', file.type);

      // التحقق من نوع الملف (يجب أن يكون صورة)
      if (!file.type.startsWith('image/')) {
        setError('الرجاء اختيار ملف صورة صالح (jpg, png, gif, etc)');
        // إعادة تعيين قيمة حقل الإدخال
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // التحقق من حجم الملف (أقل من 5 ميجابايت)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('حجم الصورة كبير جداً. الحد الأقصى هو 5 ميجابايت');
        // إعادة تعيين قيمة حقل الإدخال
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setSelectedImage(file);

      // إنشاء معاينة للصورة
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result && typeof reader.result === 'string') {
          setPreviewUrl(reader.result);
          console.log('تم إنشاء معاينة الصورة بنجاح');
        } else {
          console.error('نتيجة القراءة غير صالحة');
          setError('فشل في إنشاء معاينة الصورة');
        }
      };
      reader.onerror = () => {
        console.error('فشل في قراءة الصورة');
        setError('فشل في قراءة الصورة، يرجى المحاولة مرة أخرى');
        setSelectedImage(null);
        setPreviewUrl(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // إزالة الصورة المختارة
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setValue('image', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // تحميل الصورة باستخدام الخدمة الموحدة
  const uploadImageToCloudinary = async (file: File): Promise<string> => {
    setIsUploading(true);
    try {
      console.log('بدء عملية تحميل الصورة للمنتج...');

      // استخدام الخدمة الموحدة
      const { uploadUnifiedImage } = await import('../services/unifiedImageService');
      const imageResult = await uploadUnifiedImage({
        file: file,
        context: 'inventory',
        folder: 'inventory'
      });
      console.log('تم تحميل الصورة بنجاح، النتيجة:', imageResult);

      // تعيين قيم الروابط للمنتج
      setValue('imageUrl', imageResult.url);
      setValue('imgbbUrl', imageResult.imgbbUrl || '');
      setValue('imagekitUrl', imageResult.imagekitUrl || '');
      
      return imageResult.url;
    } catch (error) {
      console.error('خطأ في تحميل الصورة:', error);
      setError('فشل تحميل الصورة. الرجاء المحاولة مرة أخرى.');
      throw new Error('فشل تحميل الصورة: ' + (error instanceof Error ? error.message : 'خطأ غير معروف'));
    } finally {
      setIsUploading(false);
    }
  };

  // معالجة تقديم النموذج
  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError('');

    try {
      // التحقق من وجود اسم المنتج
      if (!data.name || data.name.trim() === '') {
        throw new Error('يرجى إدخال اسم المنتج');
      }

      let imageUrl = data.image;
      let imgbbUrl = data.imgbbUrl || '';
      let imagekitUrl = data.imagekitUrl || '';

      // إذا تم اختيار صورة جديدة، قم بتحميلها
      if (selectedImage) {
        try {
          console.log('تحميل الصورة الجديدة للمنتج...');
          imageUrl = await uploadImageToCloudinary(selectedImage);
          console.log('تم تحميل الصورة بنجاح، URL:', imageUrl.substring(0, 50) + '...');
          data.image = imageUrl; // تعيين الصورة مباشرة في كائن البيانات
          data.imageUrl = imageUrl;
          data.imgbbUrl = imgbbUrl;
          data.imagekitUrl = imagekitUrl;
          setValue('image', imageUrl);
          setValue('imageUrl', imageUrl);
          setValue('imgbbUrl', imgbbUrl);
          setValue('imagekitUrl', imagekitUrl);
        } catch (uploadError) {
          console.error('خطأ في تحميل الصورة:', uploadError);
          // المتابعة بدون صورة بدلاً من فشل العملية بالكامل
          setError(`فشل في تحميل الصورة، ولكن سيتم متابعة إضافة المنتج: ${uploadError instanceof Error ? uploadError.message : 'خطأ غير معروف'}`);
          // الاستمرار في العملية بدون تحميل صورة
          imageUrl = '';
          imgbbUrl = '';
          imagekitUrl = '';
        }
      }

      // التحقق من وجود كميات المخازن
      if (!data.warehouseQuantities || data.warehouseQuantities.length === 0) {
        console.log('No warehouse quantities specified in form data');

        // التحقق من وجود مخازن
        if (warehouses.length === 0) {
          console.log('No warehouses available, creating a default warehouse');

          try {
            // إنشاء مخزن افتراضي إذا لم يكن هناك مخازن
            const defaultWarehouseId = await createWarehouse('المخزن الرئيسي', 'الموقع الافتراضي');
            console.log('Created default warehouse with ID:', defaultWarehouseId);

            // إعادة تحميل المخازن
            const updatedWarehouses = await getAllWarehouses();
            console.log('Warehouses reloaded:', updatedWarehouses);

            if (updatedWarehouses.length > 0) {
              // تحديث المخازن المحلية
              setWarehouses(updatedWarehouses);

              // إضافة المخزن الافتراضي إلى الكميات
              data.warehouseId = updatedWarehouses[0].id;
              data.warehouseName = updatedWarehouses[0].name;
              data.warehouseQuantities = [{
                warehouseId: updatedWarehouses[0].id,
                warehouseName: updatedWarehouses[0].name,
                quantity: data.quantity || 0,
                location: ''
              }];
              console.log('Created default warehouse quantity:', data.warehouseQuantities);
            } else {
              throw new Error('فشل في إنشاء المخزن الافتراضي');
            }
          } catch (createError) {
            console.error('Error creating default warehouse:', createError);
            throw new Error('فشل في إنشاء المخزن الافتراضي');
          }
        } else {
          // استخدام المخزن الافتراضي الموجود
          console.log('Using default warehouse:', warehouses[0].name);
          data.warehouseQuantities = [{
            warehouseId: data.warehouseId || warehouses[0].id,
            warehouseName: data.warehouseName || warehouses[0].name,
            quantity: data.quantity || 0,
            location: ''
          }];
          console.log('Created default warehouse quantity:', data.warehouseQuantities);
        }
      }

      // حساب الكمية الإجمالية من كميات المخازن
      const totalQuantity = data.warehouseQuantities.reduce((sum, wq) => sum + (wq.quantity || 0), 0);
      console.log('Total quantity calculated from warehouse quantities:', totalQuantity);
      data.quantity = totalQuantity;

      // تحضير الصور الإضافية
      let additionalImagesPrepared: ProductImage[] = [];
      if (productImages && productImages.length > 0) {
        console.log('عدد الصور الإضافية قبل التحويل:', productImages.length);
        console.log('بيانات الصور الإضافية قبل التحويل:', JSON.stringify(productImages));
        
        // تحقق من وجود روابط URL لجميع الصور
        const validImages = productImages.filter(img => img && img.url && img.url.trim() !== '');
        if (validImages.length !== productImages.length) {
          console.warn(`تم استبعاد ${productImages.length - validImages.length} صور غير صالحة`);
          console.log('الصور غير الصالحة:', JSON.stringify(productImages.filter(img => !img || !img.url || img.url.trim() === '')));
        }

        // تحويل الصور إلى تنسيق Firestore
        additionalImagesPrepared = prepareImagesForFirestore(validImages);
        console.log('الصور الإضافية بعد التحويل (قبل الحفظ):', JSON.stringify(additionalImagesPrepared));
      } else {
        console.log('لا توجد صور إضافية للمنتج');
      }

      // تحضير بيانات المنتج للإرسال
      const inventoryItem = {
        id: '',
        name: data.name,
        sku: data.sku || `SKU-${Date.now()}`,
        barcode: data.barcode || '',
        category: data.category || '',
        categoryId: data.categoryId || '',
        subcategory: data.subcategory || '',
        subcategoryId: data.subcategoryId || '',
        purchasePrice: data.purchasePrice || 0,
        sellingPrice: data.sellingPrice || 0,
        // أسعار متعددة للمنتج
        posPrice: data.posPrice !== undefined && data.posPrice !== null ? data.posPrice : undefined,
        storePrice: data.storePrice !== undefined && data.storePrice !== null ? data.storePrice : undefined,
        wholesalePrice: data.wholesalePrice !== undefined && data.wholesalePrice !== null ? data.wholesalePrice : undefined,
        quantity: data.quantity || 0,
        totalQuantity: data.quantity || 0, // التأكد من تعيين الكمية الإجمالية أيضًا
        minQuantity: data.minQuantity || 0,
        description: data.description || '',
        supplier: data.supplier || '',
        warehouseId: data.warehouseId || warehouses[0]?.id || '',
        warehouseName: data.warehouseName || warehouses[0]?.name || '',
        warehouseQuantities: data.warehouseQuantities,
        image: imageUrl || '', // التأكد من استخدام imageUrl المحدث
        imageUrl: imageUrl || '', // استخدام نفس القيمة لكلا الحقلين
        imgbbUrl: imgbbUrl || '', // استخدام قيمة ImgBB المحدثة
        imagekitUrl: imagekitUrl || '', // استخدام قيمة ImageKit المحدثة
        additionalImages: additionalImagesPrepared, // إضافة الصور الإضافية بعد تحويلها إلى تنسيق صالح
        showInStore: data.showInStore === undefined ? false : data.showInStore,
        featured: data.featured === undefined ? false : data.featured, // منتج مميز
        bestSeller: data.bestSeller === undefined ? false : data.bestSeller, // منتج الأكثر مبيعًا
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // تسجيل معلومات الصور الإضافية للتأكد من أنها تعمل بشكل صحيح
      console.log('الصور الإضافية قبل التحويل:', productImages);
      console.log('إرسال بيانات المنتج مع الصور الإضافية:', inventoryItem.additionalImages);

      console.log('إرسال بيانات المنتج:', inventoryItem);
      const newItemId = await addInventoryItem(inventoryItem);
      console.log('تمت إضافة المنتج بنجاح برقم:', newItemId);
      setSuccess(`تمت إضافة العنصر بنجاح برقم ${newItemId}`);
      reset();
      setWarehouseQuantities([]);
      setSelectedImage(null);
      setPreviewUrl(null);
      setProductImages([]); // إعادة تعيين مصفوفة الصور الإضافية

      // النشر على فيسبوك إذا تم اختياره
      if (data.postToFacebook) {
        console.log('جاري النشر على فيسبوك...');
        const fbResult = await postProductToFacebook({ ...inventoryItem, id: newItemId });
        if (fbResult.success) {
          setSuccess(prev => `${prev} | تم النشر على فيسبوك بنجاح!`);
        } else {
          setError(prev => `${prev ? prev + ' | ' : ''}فشل النشر على فيسبوك: ${fbResult.message}`);
        }
      }

      // توجيه المستخدم إلى صفحة تفاصيل المنتج
      setTimeout(() => {
        navigate(`/admin/inventory/${newItemId}`);
      }, 2000);
    } catch (err) {
      console.error('Error adding inventory item:', err);
      setError(err instanceof Error ? err.message : 'حدث خطأ أثناء إضافة العنصر');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          إضافة عنصر جديد للمخزون
        </Typography>

        {/* رسائل الخطأ والنجاح */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {/* معلومات المنتج الأساسية */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }}>
                <Typography variant="h6">معلومات المنتج الأساسية</Typography>
              </Divider>
            </Grid>

            {/* اسم المنتج */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" error={!!errors.name}>
                <TextField
                  label="اسم المنتج *"
                  {...register('name', { required: 'اسم المنتج مطلوب' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </FormControl>
            </Grid>

            {/* رمز المنتج (SKU) */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="رمز المنتج (SKU)"
                  {...register('sku')}
                  placeholder="سيتم إنشاؤه تلقائيًا إذا ترك فارغًا"
                />
              </FormControl>
            </Grid>

            {/* الباركود */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="الباركود"
                  {...register('barcode')}
                />
              </FormControl>
            </Grid>

            {/* الفئة الرئيسية */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="category-label">الفئة</InputLabel>
                <Controller
                  name="categoryId"
                  control={control}
                  render={({ field }: { field: ControllerRenderProps<FormData, 'categoryId'> }) => (
                    <Select
                      labelId="category-label"
                      label="الفئة"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        // البحث عن اسم الفئة المقابل للمعرف
                        const selectedCategory = categories.find(cat => cat.id === e.target.value);
                        if (selectedCategory) {
                          setValue('category', selectedCategory.name);
                        }
                      }}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {/* الفئة الفرعية (تظهر فقط إذا تم اختيار فئة رئيسية) */}
            {subcategories.length > 0 && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="subcategory-label">الفئة الفرعية</InputLabel>
                  <Controller
                    name="subcategoryId"
                    control={control}
                    render={({ field }: { field: ControllerRenderProps<FormData, 'subcategoryId'> }) => (
                      <Select
                        labelId="subcategory-label"
                        label="الفئة الفرعية"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          // البحث عن اسم الفئة الفرعية المقابل للمعرف
                          const selectedSubcategory = subcategories.find(subcat => subcat.id === e.target.value);
                          if (selectedSubcategory) {
                            setValue('subcategory', selectedSubcategory.name);
                          }
                        }}
                      >
                        {subcategories.map((subcategory) => (
                          <MenuItem key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            )}

            {/* الوصف */}
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="الوصف"
                  multiline
                  rows={4}
                  {...register('description')}
                />
              </FormControl>
            </Grid>

            {/* المورد */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="المورد"
                  {...register('supplier')}
                />
              </FormControl>
            </Grid>

            {/* عرض في المتجر */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormControlLabel
                  control={
                    <Controller
                      name="showInStore"
                      control={control}
                      render={({ field }: { field: ControllerRenderProps<FormData, 'showInStore'> }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="عرض في المتجر"
                />
              </FormControl>
            </Grid>

            {/* منتج مميز */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormControlLabel
                  control={
                    <Controller
                      name="featured"
                      control={control}
                      render={({ field }: { field: ControllerRenderProps<FormData, 'featured'> }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="منتج مميز"
                />
              </FormControl>
            </Grid>

            {/* منتج الأكثر مبيعًا */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormControlLabel
                  control={
                    <Controller
                      name="bestSeller"
                      control={control}
                      render={({ field }: { field: ControllerRenderProps<FormData, 'bestSeller'> }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      )}
                    />
                  }
                  label="الأكثر مبيعًا"
                />
              </FormControl>
            </Grid>

            {/* خيار النشر على فيسبوك */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <FormControlLabel
                  control={
                    <Controller
                      name="postToFacebook"
                      control={control}
                      render={({ field }: { field: ControllerRenderProps<FormData, 'postToFacebook'> }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="primary"
                        />
                      )}
                    />
                  }
                  label="نشر على صفحة فيسبوك"
                />
              </FormControl>
            </Grid>

            {/* معلومات السعر */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2 }}>
                <Typography variant="h6">معلومات السعر</Typography>
              </Divider>
            </Grid>

            {/* سعر الشراء */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" error={!!errors.purchasePrice}>
                <TextField
                  label="سعر الشراء"
                  type="number"
                  {...register('purchasePrice', {
                    valueAsNumber: true,
                    validate: (value: number) => value >= 0 || 'يجب أن يكون سعر الشراء عدد موجب'
                  })}
                  error={!!errors.purchasePrice}
                  helperText={errors.purchasePrice?.message}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* سعر البيع */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" error={!!errors.sellingPrice}>
                <TextField
                  label="سعر البيع"
                  type="number"
                  {...register('sellingPrice', {
                    valueAsNumber: true,
                    validate: (value: number) => value >= 0 || 'يجب أن يكون سعر البيع عدد موجب'
                  })}
                  error={!!errors.sellingPrice}
                  helperText={errors.sellingPrice?.message}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* أسعار متعددة */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2 }}>
                <Typography variant="h6">أسعار متعددة (اختياري)</Typography>
              </Divider>
            </Grid>

            {/* سعر نقطة البيع (POS) */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="سعر نقطة البيع (POS)"
                  type="number"
                  {...register('posPrice', {
                    valueAsNumber: true,
                    validate: (value: number | undefined) => !value || value >= 0 || 'يجب أن يكون السعر عدد موجب'
                  })}
                  error={!!errors.posPrice}
                  helperText={errors.posPrice?.message || 'سعر البيع في نقطة البيع'}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* سعر المتجر الإلكتروني */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="سعر المتجر الإلكتروني"
                  type="number"
                  {...register('storePrice', {
                    valueAsNumber: true,
                    validate: (value: number | undefined) => !value || value >= 0 || 'يجب أن يكون السعر عدد موجب'
                  })}
                  error={!!errors.storePrice}
                  helperText={errors.storePrice?.message || 'سعر البيع في المتجر الإلكتروني'}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* سعر الجملة */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  label="سعر الجملة"
                  type="number"
                  {...register('wholesalePrice', {
                    valueAsNumber: true,
                    validate: (value: number | undefined) => !value || value >= 0 || 'يجب أن يكون السعر عدد موجب'
                  })}
                  error={!!errors.wholesalePrice}
                  helperText={errors.wholesalePrice?.message || 'سعر بيع الجملة'}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>EGP</Typography>,
                  }}
                />
              </FormControl>
            </Grid>

            {/* معلومات المخزون */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2 }}>
                <Typography variant="h6">معلومات المخزون</Typography>
              </Divider>
            </Grid>

            {/* الكمية الإجمالية */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#f8f8f8' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>
                  الكمية الإجمالية
                </Typography>
                <FormControl fullWidth variant="outlined">
                  <TextField
                    label="الكمية الإجمالية في جميع المخازن"
                    type="number"
                    value={warehouseQuantities.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                    disabled
                    InputProps={{
                      readOnly: true,
                      sx: { fontSize: '1.2rem', fontWeight: 'bold' }
                    }}
                    sx={{ bgcolor: 'white' }}
                  />
                </FormControl>
              </Paper>
            </Grid>

            <Grid container spacing={2}>
              {/* الحد الأدنى للكمية */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" error={!!errors.minQuantity}>
                  <TextField
                    label="الحد الأدنى للكمية"
                    type="number"
                    {...register('minQuantity', {
                      valueAsNumber: true,
                      validate: (value: number) => value >= 0 || 'يجب أن يكون الحد الأدنى عدد موجب'
                    })}
                    error={!!errors.minQuantity}
                    helperText={errors.minQuantity?.message}
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* كميات المخازن */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, mt: 3, bgcolor: '#f8f8f8' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>
                  كميات المخازن
                </Typography>

                {warehouses.length === 0 ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    لا توجد مخازن متاحة. يرجى إضافة مخزن أولاً.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {warehouses.map((warehouse) => (
                      <Grid item xs={12} key={warehouse.id}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            {warehouse.name}
                          </Typography>
                          <Grid container spacing={2}>
                            {/* حقل الكمية */}
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth variant="outlined">
                                <TextField
                                  label={`الكمية`}
                                  type="number"
                                  value={warehouseQuantities.find(q => q.warehouseId === warehouse.id)?.quantity || 0}
                                  onChange={(e) => {
                                    const newQuantities = [...warehouseQuantities];
                                    const index = newQuantities.findIndex(q => q.warehouseId === warehouse.id);
                                    if (index !== -1) {
                                      newQuantities[index].quantity = Number(e.target.value);
                                    } else {
                                      newQuantities.push({
                                        warehouseId: warehouse.id,
                                        warehouseName: warehouse.name,
                                        quantity: Number(e.target.value),
                                        location: ''
                                      });
                                    }
                                    handleWarehouseQuantityChange(newQuantities);
                                  }}
                                  inputProps={{ min: 0 }}
                                />
                              </FormControl>
                            </Grid>

                            {/* حقل الموقع في المخزن */}
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth variant="outlined">
                                <TextField
                                  label="الموقع في المخزن (الرف/الدرج/الكرتونة)"
                                  value={warehouseQuantities.find(q => q.warehouseId === warehouse.id)?.location || ''}
                                  onChange={(e) => {
                                    const newQuantities = [...warehouseQuantities];
                                    const index = newQuantities.findIndex(q => q.warehouseId === warehouse.id);
                                    if (index !== -1) {
                                      newQuantities[index].location = e.target.value;
                                    } else {
                                      newQuantities.push({
                                        warehouseId: warehouse.id,
                                        warehouseName: warehouse.name,
                                        quantity: 0,
                                        location: e.target.value
                                      });
                                    }
                                    handleWarehouseQuantityChange(newQuantities);
                                  }}
                                  placeholder="مثال: رف 3 / درج 2 / كرتونة A5"
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            </Grid>

            {/* الصورة الرئيسية */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2, mt: 2 }}>
                <Typography variant="h6">صورة المنتج الرئيسية</Typography>
              </Divider>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* معاينة الصورة */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      border: '1px dashed #ccc',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                      bgcolor: '#f9f9f9'
                    }}
                  >
                    {previewUrl ? (
                      <>
                        <img
                          src={previewUrl}
                          alt="معاينة الصورة"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            bgcolor: 'rgba(255,255,255,0.8)'
                          }}
                          onClick={handleRemoveImage}
                        >
                          <ClearIcon />
                        </IconButton>
                      </>
                    ) : (
                      <Typography color="textSecondary">
                        لم يتم اختيار صورة
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* زر اختيار الصورة */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <input
                      accept="image/*"
                      type="file"
                      id="image-upload"
                      style={{ display: 'none' }}
                      onChange={handleImageSelect}
                      ref={fileInputRef}
                    />
                    <label htmlFor="image-upload">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<PhotoCamera />}
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        اختيار صورة
                      </Button>
                    </label>
                    <Typography variant="caption" color="textSecondary">
                      * يفضل صورة بحجم مناسب (أقل من 5 ميجابايت)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      * الصيغ المدعومة: JPG, PNG, GIF
                    </Typography>
                  </Box>
                </Grid>
              </Box>
            </Grid>

            {/* صور إضافية للمنتج */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f8f8f8' }}>
                <Typography variant="h6" gutterBottom>
                  صور إضافية للمنتج
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  يمكنك إضافة صور متعددة للمنتج لعرضها من زوايا مختلفة أو بألوان متعددة
                </Typography>

                <MultipleImageUpload
                  images={productImages}
                  onImagesChange={setProductImages}
                  folder="inventory"
                  maxImages={10}
                />
              </Paper>
            </Grid>

            {/* أزرار الإرسال */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate('/admin/inventory')}
                  disabled={isLoading}
                >
                  إلغاء
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  onClick={handleSubmit(onSubmit)}
                  startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                  {isLoading ? 'جاري الحفظ...' : 'حفظ المنتج'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default NewAddInventoryPage;
